import { useEffect, useState, type FormEvent } from "react";
import { useLantern, useSession } from "../hooks";
import { dateChoice, dateChosen, getFilter, hasFlag, setFilter, setFlag } from "../searchQuery";
import { navigate } from "../router";
import type { SearchAnswer, SearchHit } from "../sync/api";
import { Snippet } from "../text";
import { Avatar, Card, EmptyState, Form, Page } from "./ui";
import { locale, t, tn, tx } from "../i18n";

export function SearchPanel() {
  const session = useSession();
  const state = useLantern();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [more, setMore] = useState(false);
  const [answer, setAnswer] = useState<SearchAnswer | null>(null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState("");
  // A search asked for from the top bar: /search?q=…
  const asked = new URLSearchParams(location.search).get("q") ?? "";
  useEffect(() => {
    if (!asked) return;
    setQ(asked);
    void run(asked);
  }, [asked]);

  async function run(query: string) {
    if (!query.trim()) return;
    setError("");
    setAnswer(null);
    try {
      const r = await session.api.search(query);
      setHits(r.hits);
      setMore(r.has_more);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Search failed."));
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(q);
  }
  // A chip changes the typed search and runs it.
  function change(next: string) {
    setQ(next);
    if (next.trim()) void run(next);
    else setHits(null);
  }

  const people = [...session.directory.values()]
    .filter((p) => p.status === "active" && p.kind !== "system")
    .sort((a, b) => (a.display_name || a.handle).localeCompare(b.display_name || b.handle));
  const channels = [...state.channels.values()]
    .map((c) => c.channel)
    .filter((c) => c.name)
    .sort((a, b) => a.name!.localeCompare(b.name!));
  const from = getFilter(q, "from").replace(/^@/, "");
  const inCh = getFilter(q, "in").replace(/^#/, "");
  const date = dateChosen(q);

  // ask answers the question from the user's channels, citing messages.
  async function ask() {
    if (!q.trim()) return;
    setError("");
    setAsking(true);
    try {
      const a = await session.api.answer(q);
      setAnswer(a);
      if (a.hits) setHits(a.hits);
    } catch {
      setError(t("Could not answer that."));
    } finally {
      setAsking(false);
    }
  }

  const open = (m: { channel_id: string; id: string; thread_root_id?: string | null }) =>
    navigate(m.thread_root_id ? `/c/${m.channel_id}/t/${m.thread_root_id}` : `/c/${m.channel_id}/m/${m.id}`);

  return (
    <Page className="search" title={t("Search")} description={t("Find messages in the channels you can read. Ask answers a question from them, citing its sources.")}>
      <Form onSubmit={submit} role="search" className="search-bar">
        <span className="search-icon" aria-hidden>
          ⌕
        </span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("Words, {q}phrases{q}, from:@someone in:#channel after:2026-10-01", { q: '"' })}
          aria-label={t("Search messages")}
          autoFocus
        />
        <button type="submit">{t("Search")}</button>
        <button type="button" className="secondary" onClick={ask} disabled={asking}>
          {asking ? t("Thinking…") : t("Ask")}
        </button>
      </Form>
      <div className="search-filters" role="group" aria-label={t("Filters")}>
        <select className={`chip ${from ? "on" : ""}`} value={from} onChange={(e) => change(setFilter(q, "from", e.target.value ? "@" + e.target.value : ""))} aria-label={t("From")}>
          <option value="">{t("From anyone")}</option>
          {people.map((p) => (
            <option key={p.id} value={p.handle}>
              {t("From {name}", { name: p.display_name || p.handle })}
            </option>
          ))}
        </select>
        <select className={`chip ${inCh ? "on" : ""}`} value={inCh} onChange={(e) => change(setFilter(q, "in", e.target.value ? "#" + e.target.value : ""))} aria-label={t("In")}>
          <option value="">{t("In any channel")}</option>
          {channels.map((c) => (
            <option key={c.id} value={c.name}>
              {t("In #{channel}", { channel: c.name! })}
            </option>
          ))}
        </select>
        <select
          className={`chip ${date ? "on" : ""}`}
          value={date === "custom" ? "custom" : date}
          onChange={(e) => {
            let next = setFilter(setFilter(setFilter(q, "on", ""), "after", ""), "before", "");
            const c = dateChoice(e.target.value);
            if (c) next = setFilter(next, c[0], c[1]);
            change(next);
          }}
          aria-label={t("Date")}
        >
          <option value="">{t("Any time")}</option>
          <option value="today">{t("Today")}</option>
          <option value="yesterday">{t("Yesterday")}</option>
          <option value="week">{t("Last 7 days")}</option>
          <option value="month">{t("Last 30 days")}</option>
          {date === "custom" && <option value="custom">{t("Dates you typed")}</option>}
        </select>
        {(
          [
            ["has:file", t("Has files")],
            ["has:link", t("Has links")],
            ["is:thread", t("In threads")],
            ["is:saved", t("Saved")],
          ] as const
        ).map(([flag, label]) => (
          <button key={flag} type="button" className={`chip ${hasFlag(q, flag) ? "on" : ""}`} aria-pressed={hasFlag(q, flag)} onClick={() => change(setFlag(q, flag, !hasFlag(q, flag)))}>
            {label}
          </button>
        ))}
      </div>
      {error && <p className="error">{error}</p>}
      {answer && answer.reason === "no_model" && <p className="note">{t("Answers need a model; here are the matching messages.")}</p>}
      {answer && !answer.reason && (
        <Card title={t("Answer")} className="answer">
          <p className="answer-text" aria-label={t("Answer")}>
            {answer.answer.split(/(\[\d+\])/).map((part, i) => {
              const n = part.match(/^\[(\d+)\]$/)?.[1];
              const cite = n ? answer.citations.find((c) => c.n === Number(n)) : undefined;
              return cite ? (
                <button key={i} className="cite" onClick={() => open(cite.message)} title={cite.message.text}>
                  {n}
                </button>
              ) : (
                <span key={i}>{part}</span>
              );
            })}
          </p>
          {answer.citations.length > 0 && (
            <ol className="sources">
              {answer.citations.map((c) => (
                <li key={c.n} value={c.n}>
                  <button className="link" onClick={() => open(c.message)}>
                    {session.channelName(c.message.channel_id)} · {session.name(c.message.author.id)}: {c.message.text.slice(0, 120)}
                  </button>
                </li>
              ))}
            </ol>
          )}
        </Card>
      )}
      {hits?.length === 0 && (
        <Card>
          <EmptyState icon="🔍" title={t("No messages match")}>
            {t("Try other words, or fewer of them.")}
          </EmptyState>
        </Card>
      )}
      {hits && hits.length > 0 && (
        <Card title={more ? t("{n}+ messages", { n: hits.length }) : tn(hits.length, "{n} message", "{n} messages")}>
          <ul className="rows search-results">
            {hits.map((h) => (
              <li key={h.message.id}>
                <Avatar id={h.message.author.id} name={session.name(h.message.author.id)} size={32} />
                <button className="inbox-message" onClick={() => open(h.message)}>
                  <span className="where">
                    {tx("{name} in {channel}", { name: <strong>{session.name(h.message.author.id)}</strong>, channel: session.channelName(h.message.channel_id) })}
                    <span className="muted"> · {new Date(h.message.created_at).toLocaleDateString(locale, { month: "short", day: "numeric" })}</span>
                  </span>
                  <span className="text snippet">
                    <Snippet text={h.snippet} name={(id) => session.name(id)} me={session.store.getSnapshot().me?.id} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </Page>
  );
}
